import React, { lazy } from "react";
import { Navigate } from "react-router-dom";
import { ASSESSMENT_PROPOSALS_ENABLED } from "./constants/featureFlags";
import NotFound from "./pages/not-found";
import InvestmentProposalBulkReRegister from "./pages/users/bulk-re-register";
import InvestmentProposalBulkRegister from "./pages/users/bulk-register";
const BasicPageFrame = lazy(async () => await import("./components/Templates/BasicPageFrame"));
const EntrancePageFrame = lazy(async () => await import("./components/Templates/EntrancePageFrame"));

const Login = lazy(async () => await import("./pages/sign-in"));
const Register = lazy(async () => await import("./pages/sign-up"));
const ResetPasswordPage = lazy(async () => await import("./pages/reset-password"));
const NewPasswordPage = lazy(async () => await import("./pages/new-password"));

const AssessmentProposals = lazy(async () => await import("./pages/properties"));
const AssessmentProposalsProposed = lazy(async () => await import("./pages/properties/proposed"));
const AssessmentProposalsRegisterPage = lazy(async () => await import("./pages/properties/[propertyId]/register"));
const EditAssessmentProposal = lazy(async () => await import("./pages/properties/[propertyId]/edit"));

const InvestmentProposals = lazy(async () => await import("./pages/users"));
const InvestmentProposalsProposed = lazy(async () => await import("./pages/users/proposed"));
const RegisterInvestmentProposal = lazy(async () => await import("./pages/users/[userId]/register"));
const EditInvestmentProposal = lazy(async () => await import("./pages/users/[userId]/edit"));

const AiOfferBatchList = lazy(async () => await import("./pages/users/ai-offers"));
const AiOfferBatchConfirm = lazy(async () => await import("./pages/users/ai-offers/[batchId]"));

const Matches = lazy(async () => await import("./pages/matches"));

const ApplyContract = lazy(async () => await import("./pages/apply-contract"));

const Settings = lazy(async () => await import("./pages/settings"));

// 査定提案の提供停止中（Trello #147）は、新規作成につながる画面（未提案の物件・送信予定・登録）を
// 投資提案へ逃がす。提案済みの一覧・詳細はメニュー・パンくずには出さず、URL と提案履歴
// （ユーザー詳細・マッチ詳細）からのみ到達できる閲覧専用の画面として残す。
const assessmentProposalEntryRoutes = ASSESSMENT_PROPOSALS_ENABLED
  ? [
      {
        text: "未提案の物件",
        path: "",
        element: <AssessmentProposals />,
      },
      {
        text: "送信予定のオファー",
        path: "scheduled",
        element: <AssessmentProposalsProposed key="assessment-scheduled" statusFilter="scheduled" />,
      },
      {
        text: "査定提案",
        path: ":propertyId/assessment-proposals/register",
        element: <AssessmentProposalsRegisterPage />,
      },
    ]
  : [
      { text: "", path: "", element: <Navigate replace to="/users" /> },
      { text: "", path: "scheduled", element: <Navigate replace to="/users" /> },
      { text: "", path: ":propertyId/assessment-proposals/register", element: <Navigate replace to="/users" /> },
    ];

export const routerPaths = [
  {
    path: "",
    element: <BasicPageFrame />,
    children: [
      {
        path: "",
        // react router 的にリダイレクトが非推奨でも使いたい
        // TODO: top page が決まったら変更する
        element: <Navigate replace to="/users" />,
      },
    ],
  },
  {
    path: "/",
    element: <EntrancePageFrame />,
    children: [
      {
        path: "sign-up",
        element: <Register />,
      },
      {
        path: "sign-in",
        element: <Login />,
      },
      {
        path: "reset-password",
        element: <ResetPasswordPage />,
      },
      {
        path: "new-password",
        element: <NewPasswordPage />,
      },
    ],
  },
  {
    path: "/properties",
    text: "査定提案",
    element: <BasicPageFrame />,
    children: [
      ...assessmentProposalEntryRoutes,
      {
        text: "提案済みの物件",
        path: "proposed",
        element: <AssessmentProposalsProposed key="assessment-proposed" />,
      },
      {
        text: "提案の詳細",
        path: "proposed/:proposalId",
        element: <EditAssessmentProposal />,
      },
    ],
  },
  {
    path: "/users",
    text: "投資提案",
    element: <BasicPageFrame />,
    children: [
      {
        text: "未提案のユーザー",
        path: "",
        element: <InvestmentProposals />,
      },
      {
        text: "提案済みのユーザー",
        path: "proposed",
        element: <InvestmentProposalsProposed key="investment-proposed" />,
      },
      {
        text: "送信予定のオファー",
        path: "scheduled",
        element: <InvestmentProposalsProposed key="investment-scheduled" statusFilter="scheduled" />,
      },
      {
        text: "一括投資提案",
        path: "bulk-register",
        element: <InvestmentProposalBulkRegister />,
      },
      {
        text: "AIオファー",
        path: "ai-offers",
        element: <AiOfferBatchList />,
      },
      {
        text: "AIオファー内容確認",
        path: "ai-offers/:batchId",
        element: <AiOfferBatchConfirm />,
      },
      {
        text: "一括再投資提案",
        path: "bulk-re-register",
        element: <InvestmentProposalBulkReRegister />,
      },
      {
        text: "提案の詳細",
        path: "proposed/:proposalId",
        element: <EditInvestmentProposal />,
      },
      {
        text: "投資提案",
        path: ":userId/investment-proposals/register",
        element: <RegisterInvestmentProposal />,
      },
    ],
  },
  {
    path: "/matches",
    text: "マッチしたユーザー",
    element: <BasicPageFrame />,
    children: [
      {
        text: "ユーザー一覧",
        path: "",
        element: <Matches />,
      },
    ],
  },
  {
    path: "/apply-contract",
    text: "成約申請",
    element: <BasicPageFrame />,
    children: [
      {
        text: "成約申請",
        path: "",
        element: <ApplyContract />,
      },
    ],
  },
  {
    path: "/settings",
    text: "設定",
    element: <BasicPageFrame />,
    children: [
      {
        text: "プロフィール編集",
        path: "",
        element: <Settings />,
      },
    ],
  },
  {
    path: "*",
    element: <NotFound />,
  },
];
